import { Modal } from '../../../components/ui/Modal';
import { useState, useEffect, useCallback } from 'react';
import { Loader2, User, Car, CheckCircle, AlertCircle, RefreshCw, UserPlus } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import { motion, AnimatePresence } from 'framer-motion';
import { VRMLookup, type FinalVehicleData, type LinkedCustomerData } from '../../../components/vehicles/VRMLookup';
import { CustomerTypeahead, type CustomerSearchResult } from './components/CustomerTypeahead';
import { errorMessage, reportError } from '../../../lib/errors';
import JobCard, { type Job } from './JobCard';
import { completeCreatedJobFlow } from './jobWorkflow';
import { refreshMotHistoryWithin } from '../../../services/vrmService';
import { motOdometerOffer } from '../../../lib/vehicle';
import { MotMileageOffer } from '../../../components/vehicles/MotMileageOffer';
import { clearJobDraft, isDraftWorthKeeping, readJobDraft, saveJobDraft } from '../../../lib/jobDraft';

interface NewJobModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (jobId: string) => void | Promise<void>;
    createdJob?: Job;
    continueInWorkspace?: boolean;
    onAddPart?: (jobId: string, jobCreatedAt: string) => void;
    onAddLabour?: (jobId: string, jobCreatedAt: string) => void;
    onGenerateInvoice?: (jobId: string, invoiceId?: string) => void;
    onWalkaround?: (jobId: string) => void;
    onRefresh?: () => void;
    /** Reopen the workspace of a job created earlier instead of a blank form. */
    resumeJobId?: string | null;
    /** A failed write from the workspace (e.g. + Part), shown above the job. */
    workspaceError?: string | null;
    onDismissWorkspaceError?: () => void;
}

/** One prior job on this vehicle, with the line items its total is summed from. */
interface HistoryRow {
    id: string;
    created_at: string;
    status: string;
    description: string | null;
    job_items: { quantity: number | null; unit_price: number | null }[] | null;
}

interface FormData {
    // Customer
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    // Vehicle (auto-filled by VRMLookup, editable)
    vrm: string;
    make: string;
    model: string;
    colour: string;
    mileage: string;
    year: string;
    engine: string;
    fuelType: string;
    vehicleSource?: 'dvla' | 'db' | 'manual';
    // Job
    jobDescription: string;
}

interface JobHistory {
    id: string;
    created_at: string;
    status: string;
    description: string;
    total: number;
}

interface ServiceTemplate {
    id: string;
    title: string;
    description: string;
    estimated_price: number;
    estimated_duration_minutes: number;
}

const initialFormData: FormData = {
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    vrm: '',
    make: '',
    model: '',
    colour: '',
    mileage: '',
    year: '',
    engine: '',
    fuelType: '',
    vehicleSource: undefined,
    jobDescription: ''
};

export default function NewJobModal({ isOpen, onClose, onSuccess, createdJob, continueInWorkspace = false, onAddPart, onAddLabour, onGenerateInvoice, onWalkaround, onRefresh, resumeJobId, workspaceError, onDismissWorkspaceError }: NewJobModalProps) {
    const { activeOrgId } = useVertical();
    // What the form held when it was last closed or the app died, restored so
    // nothing typed is lost (lib/jobDraft.ts). Read once, on mount: the hosts
    // (NewJobFlow) mount this modal fresh each time New Job is opened.
    const [draft] = useState(() => (!resumeJobId && activeOrgId ? readJobDraft(activeOrgId) : null));
    const [restoredDraft, setRestoredDraft] = useState(!!draft);
    const [formData, setFormData] = useState<FormData>(() => (draft ? { ...initialFormData, ...draft.form } : initialFormData));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [templates, setTemplates] = useState<ServiceTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [vehicleHistory, setVehicleHistory] = useState<JobHistory[]>([]);
    const [vehicleConfirmed, setVehicleConfirmed] = useState(draft?.vehicleConfirmed ?? false);
    const [linkedCustomer, setLinkedCustomer] = useState<LinkedCustomerData | null>(
        draft?.linkedCustomer ? { ...draft.linkedCustomer, full_name: draft.linkedCustomer.full_name ?? '' } : null,
    );
    const [selectedCustomerVehicles, setSelectedCustomerVehicles] = useState<CustomerSearchResult['vehicles']>(undefined);
    const [existingMatch, setExistingMatch] = useState<{
        customer: { id: string; full_name: string; phone: string | null; email: string | null };
        matchedBy: 'email' | 'phone';
        differs: 'phone' | 'email';
    } | null>(null);
    // Soft "possible duplicate" advisory: existing customers with the same name.
    // Purely advisory — the user reuses one or continues as a new (separate) record.
    const [nameMatches, setNameMatches] = useState<Array<{ id: string; full_name: string; phone: string | null; email: string | null }>>([]);
    // Soft note: existing customer(s) already on record for this reg (shared / sold
    // cars are legitimate — this only guards against a mistyped VRM).
    const [vrmOtherOwners, setVrmOtherOwners] = useState<string[]>([]);
    const [createdJobId, setCreatedJobId] = useState<string | null>(resumeJobId ?? null);
    // The odometer from the vehicle's last MOT, OFFERED for the mileage box —
    // never written into it on its own. See the note on the offer button below
    // for why the difference matters.
    const [motOffer, setMotOffer] = useState<{ miles: number; testDate: string | null } | null>(null);
    // Set to the MOT test date once the user has TAKEN that offer, so the field
    // can say where the number came from. Cleared the moment they type their own.
    const [mileageFromMotOn, setMileageFromMotOn] = useState<string | null>(null);

    // Fetch Templates
    useEffect(() => {
        if (activeOrgId) {
            supabase
                .from('service_templates')
                .select('*')
                .eq('org_id', activeOrgId)
                .order('title')
                .then(({ data }) => setTemplates(data || []));
        }
    }, [activeOrgId]);

    const handleTemplateChange = (templateId: string) => {
        const template = templates.find(t => t.id === templateId);
        if (template) {
            setSelectedTemplate(templateId);
            setFormData(prev => ({
                ...prev,
                jobDescription: `${template.title} (£${template.estimated_price}): ${template.description}`
            }));
        } else {
            setSelectedTemplate('');
        }
    };

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFormData(initialFormData);
            setError(null);
            setVehicleHistory([]);
            setVehicleConfirmed(false);
            setLinkedCustomer(null);
            setExistingMatch(null);
            setNameMatches([]);
            setVrmOtherOwners([]);
            setCreatedJobId(null);
            setMotOffer(null);
            setMileageFromMotOn(null);
            setRestoredDraft(false);
        }
    }, [isOpen]);

    // Keep the draft in step with every change until the job exists. A blank
    // form is never written over a saved draft (the org can load after the
    // first render); "Start fresh" is what discards one.
    useEffect(() => {
        if (!isOpen || !activeOrgId || createdJobId || !isDraftWorthKeeping(formData)) return;
        saveJobDraft(activeOrgId, { form: formData, linkedCustomer, vehicleConfirmed });
    }, [isOpen, activeOrgId, createdJobId, formData, linkedCustomer, vehicleConfirmed]);

    const startFresh = () => {
        if (activeOrgId) clearJobDraft(activeOrgId);
        setFormData(initialFormData);
        setLinkedCustomer(null);
        setVehicleConfirmed(false);
        setVehicleHistory([]);
        setExistingMatch(null);
        setNameMatches([]);
        setVrmOtherOwners([]);
        setSelectedTemplate('');
        setSelectedCustomerVehicles(undefined);
        setMotOffer(null);
        setMileageFromMotOn(null);
        setRestoredDraft(false);
    };

    // Soft name-based duplicate check (advisory). Surfaces existing customers with
    // the same name so the user can reuse one instead of creating a duplicate —
    // but never merges automatically (a name is not a unique identity).
    const checkForNameMatch = useCallback(async () => {
        if (!activeOrgId || linkedCustomer) { setNameMatches([]); return; }
        const name = formData.customerName.trim();
        if (name.length < 2) { setNameMatches([]); return; }
        try {
            const { data, error: fetchError } = await supabase
                .from('customers_decrypted')
                .select('id, full_name, phone, email')
                .eq('org_id', activeOrgId)
                .ilike('full_name', name)
                .limit(5);
            if (fetchError) reportError(fetchError, 'new-job-modal.customers-decrypted');
            setNameMatches(data || []);
        } catch {
            setNameMatches([]);
        }
    }, [activeOrgId, formData.customerName, linkedCustomer]);

    const handleUseNameMatch = (c: { id: string; full_name: string; phone: string | null; email: string | null }) => {
        setLinkedCustomer({ id: c.id, full_name: c.full_name, phone: c.phone, email: c.email });
        setFormData(prev => ({
            ...prev,
            customerName: c.full_name || prev.customerName,
            customerPhone: c.phone || prev.customerPhone,
            customerEmail: c.email || prev.customerEmail,
        }));
        setNameMatches([]);
        setExistingMatch(null);
    };

    // Partial match detection: check if email or phone matches an existing customer
    // but the other field differs (e.g. returning customer with new phone number)
    const checkForPartialMatch = useCallback(async () => {
        if (!activeOrgId || linkedCustomer) return; // skip if already linked via typeahead

        const email = formData.customerEmail.trim();
        const phone = formData.customerPhone.trim();
        if (!email && !phone) { setExistingMatch(null); return; }

        try {
            // Exact contact lookups go through the keyed blind index, not through
            // `.eq('email', ...)` on the plaintext column — that column is
            // encrypted at rest (20260810211000) and emptied by the purge script,
            // at which point an equality filter matches nothing and this warning
            // silently stops appearing.
            //
            // The RPC also normalises both sides (lower-cased email, digits-only
            // phone), so "Bob@Example.com" now matches a stored "bob@example.com"
            // and "07700 900123" matches "07700900123". The old equality filter
            // was byte-exact and missed both.
            //
            // Still only a WARNING: 20260716200000 is explicit that the database
            // must never decide two people are the same person. This surfaces a
            // possible duplicate and the user chooses.
            const lookupCustomer = async (byEmail: string | null, byPhone: string | null) => {
                const { data: id, error: idError } = await supabase.rpc('fn_find_my_customer_id_by_contact', {
                    p_org_id: activeOrgId,
                    p_email: byEmail,
                    p_phone: byPhone,
                });
                if (idError) reportError(idError, 'new-job-modal.find-my-customer-id-by-contact');
                if (!id) return null;
                const { data: row, error: rowError } = await supabase
                    .from('customers_decrypted')
                    .select('id, full_name, phone, email')
                    .eq('id', id)
                    .maybeSingle();
                if (rowError) reportError(rowError, 'new-job-modal.customers-decrypted');
                return row;
            };

            if (email) {
                const byEmail = await lookupCustomer(email, null);
                if (byEmail && phone && byEmail.phone && byEmail.phone !== phone) {
                    setExistingMatch({ customer: byEmail, matchedBy: 'email', differs: 'phone' });
                    return;
                }
            }

            if (phone) {
                const byPhone = await lookupCustomer(null, phone);
                if (byPhone && email && byPhone.email && byPhone.email !== email) {
                    setExistingMatch({ customer: byPhone, matchedBy: 'phone', differs: 'email' });
                    return;
                }
            }

            setExistingMatch(null);
        } catch {
            // No match found or error — that's fine
            setExistingMatch(null);
        }
    }, [activeOrgId, formData.customerEmail, formData.customerPhone, linkedCustomer]);

    // Handle "Update existing customer" action
    const handleUpdateExisting = () => {
        if (!existingMatch) return;
        setLinkedCustomer({
            id: existingMatch.customer.id,
            full_name: existingMatch.customer.full_name,
            phone: existingMatch.customer.phone,
            email: existingMatch.customer.email,
        });
        // Keep the form data as-is (user's new values will be sent to RPC which updates them)
        setExistingMatch(null);
    };

    // Handle "Create new customer" action
    const handleCreateNew = () => {
        setLinkedCustomer(null);
        setExistingMatch(null);
    };

    // === VRMLookup Callbacks ===

    const handleVehicleConfirmed = async (vehicle: FinalVehicleData) => {
        setVehicleConfirmed(true);

        // The odometer at the vehicle's last MOT, which the lookup card has
        // already shown the user. It is OFFERED below the mileage box, one tap
        // to accept — it is deliberately NOT written into the box for them.
        //
        // THIS DISTINCTION IS THE WHOLE POINT. Whatever ends up in that box is
        // sent as `p_vehicle_mileage`, and `fn_create_job_with_details` writes
        // it to `vehicles.mileage` — the column that pre-fills "current
        // mileage" on the NEXT job and that service history reports as a
        // reading taken from the vehicle. An MOT figure can be eleven months
        // old, so a silent pre-fill would put a stale number there for anyone
        // who submitted the form without touching it, which is exactly what
        // migration 20260904090000 exists to prevent.
        //
        // A tap is a person saying "yes, that is the reading I mean". The
        // database still never decides it (the standing rule from 20260716200000).
        // Reads a saved vehicle's mot_last_* columns as well as a fresh lookup.
        const storedMileage = vehicle.mileage != null ? vehicle.mileage.toString() : '';
        setMotOffer(motOdometerOffer(vehicle));
        setMileageFromMotOn(null);

        // Map DVLA/DB fields into formData
        setFormData(prev => ({
            ...prev,
            vrm: vehicle.vrm,
            make: vehicle.make || '',
            model: vehicle.model || '',
            colour: vehicle.colour || '',
            year: String(vehicle.year || vehicle.yearOfManufacture || ''),
            engine: vehicle.engine || (vehicle.engineCapacity ? `${vehicle.engineCapacity}cc` : ''),
            mileage: storedMileage,
            fuelType: vehicle.fuelType || vehicle.fuel_type || '',
            vehicleSource: vehicle.source || prev.vehicleSource,
        }));

        // Fetch vehicle history if we have a DB id.
        //
        // This used to select `description:customer_notes, total:estimated_total`.
        // Neither column exists on `jobs` — it has description / job_description /
        // status_note and NO total column at all — so PostgREST answered 400 every
        // time and the panel rendered blank forever. The error was discarded by
        // destructuring only `data`, which is why it never surfaced anywhere,
        // Sentry included. Totals come from job_items, since a job has a value
        // whether or not it was ever invoiced.
        if (vehicle.id) {
            const { data: history, error: historyError } = await supabase
                .from('jobs')
                .select('id, created_at, status, description, job_items(quantity, unit_price)')
                .eq('vehicle_id', vehicle.id)
                .is('deleted_at', null)
                .order('created_at', { ascending: false })
                .limit(3);

            if (historyError) {
                console.error('Vehicle history lookup failed', historyError);
                Sentry.captureException(historyError, { tags: { area: 'new-job.vehicle-history' } });
            }

            if (history) {
                setVehicleHistory((history as HistoryRow[]).map((h) => ({
                    id: h.id,
                    created_at: h.created_at,
                    status: h.status,
                    description: h.description || 'No description',
                    total: (h.job_items ?? []).reduce(
                        (sum, i) => sum + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
                        0
                    )
                })));
            }

            // Which customer(s) is this reg already on record for? (typo guard)
            const { data: owners, error: ownersError } = await supabase
                .from('jobs')
                .select('customers(full_name)')
                .eq('vehicle_id', vehicle.id)
                .not('customer_id', 'is', null);
            if (ownersError) reportError(ownersError, 'new-job-modal.jobs');
            const names = Array.from(new Set(
                (owners || []).map((o: any) => o.customers?.full_name).filter(Boolean)
            )) as string[];
            setVrmOtherOwners(names);
        } else {
            setVrmOtherOwners([]);
        }
    };

    const handleCustomerFound = (customer: LinkedCustomerData) => {
        setLinkedCustomer(customer);
        // Auto-fill customer fields ONLY if currently empty
        setFormData(prev => ({
            ...prev,
            customerName: prev.customerName || customer.full_name || '',
            customerPhone: prev.customerPhone || customer.phone || '',
            customerEmail: prev.customerEmail || customer.email || '',
        }));
    };

    const handleCustomerSelect = (customer: CustomerSearchResult) => {
        setLinkedCustomer({
            id: customer.id,
            full_name: customer.full_name,
            phone: customer.phone,
            email: customer.email,
        });
        setFormData(prev => ({
            ...prev,
            customerName: customer.full_name,
            customerPhone: customer.phone || '',
            customerEmail: customer.email || '',
        }));
        setSelectedCustomerVehicles(customer.vehicles);
    };

    const handleVehicleQuickSelect = (v: NonNullable<CustomerSearchResult['vehicles']>[0]) => {
        setFormData(prev => ({
            ...prev,
            vrm: v.vrm,
            make: v.make,
            model: v.model,
            colour: v.colour || '',
            year: v.year ? v.year.toString() : '',
            engine: v.engine || '',
            fuelType: v.fuel_type || '',
            // Pre-fill the last-known mileage stored on the vehicle so the user
            // doesn't have to re-enter it for a returning car (they can still
            // overwrite it with a fresher reading).
            mileage: v.mileage != null ? String(v.mileage) : '',
            vehicleSource: 'db'
        }));
        setMotOffer(null);
        setMileageFromMotOn(null);
        setVehicleConfirmed(true);
    };

    // Conflict detection
    const getConflict = () => {
        if (!linkedCustomer) return null;

        const nameMismatch = formData.customerName && linkedCustomer.full_name &&
            formData.customerName.trim().toLowerCase() !== linkedCustomer.full_name.trim().toLowerCase();

        if (nameMismatch) {
            return `Warning: This vehicle is registered to "${linkedCustomer.full_name}". Saving will link it to "${formData.customerName}".`;
        }
        return null;
    };

    const conflictWarning = getConflict();

    // Form submission
    const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
        if (e) e.preventDefault();
        setError(null);

        // Validation
        if (!formData.customerName.trim()) { setError('Customer name is required'); return; }
        if (!formData.customerPhone.trim()) { setError('Customer phone is required'); return; }
        if (!formData.customerEmail.trim()) { setError('Customer email is required'); return; }
        if (!formData.vrm.trim()) { setError('Vehicle registration (VRM) is required'); return; }
        if (!formData.make.trim()) { setError('Vehicle make is required'); return; }
        if (!formData.model.trim()) { setError('Vehicle model is required'); return; }
        if (!formData.year.trim()) { setError('Vehicle year is required'); return; }
        if (!activeOrgId) { setError('Organisation not found'); return; }

        setLoading(true);

        try {
            const { data: newJobId, error: rpcError } = await supabase.rpc('fn_create_job_with_details', {
                p_org_id: activeOrgId,
                p_customer_id: linkedCustomer?.id || null,
                p_customer_name: formData.customerName.trim(),
                p_customer_phone: formData.customerPhone.trim(),
                p_customer_email: formData.customerEmail.trim(),
                p_vehicle_vrm: formData.vrm.trim(),
                p_vehicle_make: formData.make.trim(),
                p_vehicle_model: formData.model.trim(),
                p_vehicle_colour: formData.colour.trim() || null,
                p_vehicle_mileage: formData.mileage ? parseInt(formData.mileage) : null,
                p_vehicle_year: formData.year ? parseInt(formData.year) : null,
                p_vehicle_engine: formData.engine.trim() || null,
                p_vehicle_fuel_type: formData.fuelType.trim() || null,
                p_job_description: formData.jobDescription.trim() || null
            });

            if (rpcError) throw rpcError;
            if (typeof newJobId !== 'string' || !newJobId) {
                throw new Error('The job was created but no job ID was returned.');
            }
            // The job now exists in the database; the draft has done its job.
            clearJobDraft(activeOrgId);

            // Pull the vehicle's MOT history in now that it exists.
            //
            // This flow can create a brand-new vehicle, and nothing here used to
            // fetch its MOT record — so the customer's profile showed no MOT
            // date, no advisories and no mileage until someone found the Refresh
            // MOT button on the vehicle card. The RPC returns only the job id,
            // hence the read-back for the vehicle it created or matched.
            //
            // Bounded and non-fatal: the job is committed either way, and the
            // nightly sweep picks up anything that does not land.
            const { data: createdJob, error: createdJobError } = await supabase
                .from('jobs')
                .select('vehicle_id')
                .eq('id', newJobId)
                .maybeSingle();
            if (createdJobError) reportError(createdJobError, 'new-job.jobs-vehicle-id');
            if (createdJob?.vehicle_id) await refreshMotHistoryWithin(createdJob.vehicle_id);

            await completeCreatedJobFlow(newJobId, continueInWorkspace, {
                setCreatedJobId,
                onSuccess: async (jobId) => { await onSuccess(jobId); },
                onClose,
            });
        } catch (err) {
            // Surface a friendly message for the free-tier limit
            if (errorMessage(err)?.includes('FREE_TIER_LIMIT_REACHED')) {
                setError('You\u2019ve used all 10 free jobs this month. Upgrade to Pro for unlimited jobs.');
            } else {
                setError(errorMessage(err) || 'Failed to create job');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={createdJobId ? 'Job created — continue working' : 'New Job'}
            subtitle="Customer, vehicle and work details"
            editor
            footer={!createdJobId ? (<div className="flex justify-end">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading || !formData.vrm.trim()}
                            className="fd-btn fd-btn-go fd-btn-lg w-full sm:w-auto"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Creating Job...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-5 h-5" />
                                    {continueInWorkspace ? 'Create Job & Continue' : 'Create Job'}
                                </>
                            )}
                        </button>
                    </div>) : undefined}
        >
                {createdJobId ? (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 rounded-lg border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">
                            <CheckCircle className="w-5 h-5 shrink-0" />
                            <span>The job is saved. Add parts, labour and the walkaround here, then generate the invoice at the bottom.</span>
                        </div>
                        {workspaceError && (
                            <div role="alert" className="flex items-start justify-between gap-3 rounded-lg border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                                <span className="min-w-0 break-words">{workspaceError}</span>
                                {onDismissWorkspaceError && (
                                    <button
                                        type="button"
                                        onClick={onDismissWorkspaceError}
                                        aria-label="Dismiss"
                                        className="min-h-[44px] min-w-[44px] -my-2 -mr-2 shrink-0 text-danger"
                                    >
                                        &times;
                                    </button>
                                )}
                            </div>
                        )}
                        {createdJob?.id === createdJobId && onAddPart && onAddLabour && onGenerateInvoice && onWalkaround && onRefresh ? (
                            <JobCard
                                job={createdJob}
                                viewMode="list"
                                defaultExpanded
                                onAddPart={onAddPart}
                                onAddLabour={onAddLabour}
                                onComplete={() => undefined}
                                onGenerateInvoice={onGenerateInvoice}
                                onWalkaround={onWalkaround}
                                onRefresh={onRefresh}
                            />
                        ) : (
                            <div className="flex items-center justify-center gap-3 py-12 text-fg-3">
                                <Loader2 className="w-5 h-5 animate-spin text-accent" />
                                Loading the new job workspace…
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full min-h-[44px] rounded-lg border border-tint/10 bg-tint/5 text-sm font-bold text-fg-2 hover:bg-tint/10"
                        >
                            Close — the job is saved
                        </button>
                    </div>
                ) : (
                <div className="space-y-6">

                    {restoredDraft && (
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-info/20 bg-info/10 px-4 py-3 text-sm text-info">
                            <span className="min-w-0 break-words">
                                Picked up where you left off{formData.vrm ? <> — <span className="font-mono text-fg">{formData.vrm}</span></> : ''}.
                            </span>
                            <button
                                type="button"
                                onClick={startFresh}
                                className="min-h-[44px] shrink-0 rounded-lg border border-tint/10 bg-tint/5 px-3 text-xs font-bold text-fg-2"
                            >
                                Start fresh
                            </button>
                        </div>
                    )}

                    {/* === VRM Lookup (Unified Component) === */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-fg-4 uppercase tracking-wider">
                            Vehicle Registration (VRM)
                        </label>
                        <VRMLookup
                            onVehicleConfirmed={handleVehicleConfirmed}
                            onVehicleCleared={() => {
                                setFormData(prev => ({ ...prev, vrm: '', make: '', model: '', colour: '', mileage: '', year: '', engine: '', fuelType: '', vehicleSource: undefined }));
                                setVehicleConfirmed(false);
                                setVehicleHistory([]);
                                setMotOffer(null);
                                setMileageFromMotOn(null);
                            }}
                            onCustomerFound={handleCustomerFound}
                            onSkipToManual={(vrm) => {
                                // Preserve the VRM the user typed so the manual form
                                // doesn't immediately fail validation on submit.
                                setFormData(prev => ({ ...prev, vrm: vrm || prev.vrm, vehicleSource: 'manual' }));
                                setVehicleConfirmed(false);
                            }}
                            confirmButtonLabel="Apply Vehicle to Job"
                        />
                    </div>

                    {/* Vehicle History (if found from DB) */}
                    {vehicleHistory.length > 0 && (
                        <div className="p-3 bg-card/50 rounded-lg border border-tint/5">
                            <h4 className="text-xs font-bold text-fg-4 uppercase mb-2">Recent Vehicle History</h4>
                            <div className="space-y-2">
                                {vehicleHistory.map(job => (
                                    <div key={job.id} className="flex justify-between items-center text-xs">
                                        <div className="flex gap-2 text-fg-2">
                                            <span className="font-mono text-fg-4">{new Date(job.created_at).toLocaleDateString()}</span>
                                            <span className="truncate max-w-[200px]">{job.description}</span>
                                        </div>
                                        <div className={`px-1.5 py-0.5 rounded uppercase tracking-wider font-bold ${
                                            job.status === 'completed' ? 'text-success bg-success/10' : 'text-fg-3 bg-raised'
                                        }`}>
                                            {job.status}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    {/* Customer Details */}
                    <div className="min-w-0 bg-tint/5 rounded-lg p-4 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-bold text-fg-2 flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Customer Details
                                {linkedCustomer && (
                                    <span className="text-[10px] font-bold text-success bg-success/10 px-2 py-0.5 rounded uppercase">Auto-filled</span>
                                )}
                            </h3>
                        </div>

                        {/* Customer Search / Typeahead */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-fg-3 mb-2">Search Existing Customer</label>
                            <CustomerTypeahead onSelect={handleCustomerSelect} />
                            
                            {selectedCustomerVehicles && selectedCustomerVehicles.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-tint/10">
                                    <label className="block text-xs font-medium text-fg-3 mb-2">Select Customer's Vehicle</label>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedCustomerVehicles.map(v => (
                                            <button
                                                key={v.vrm}
                                                type="button"
                                                onClick={() => handleVehicleQuickSelect(v)}
                                                className="flex flex-col items-start bg-tint/5 hover:bg-tint/10 border border-tint/10 rounded px-3 py-2 transition-colors text-left"
                                            >
                                                <span className="font-mono text-sm text-accent">{v.vrm}</span>
                                                <span className="text-xs text-fg-2">{v.make} {v.model}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                    Full Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.customerName}
                                    onChange={e => { setFormData({ ...formData, customerName: e.target.value }); setNameMatches([]); }}
                                    onBlur={checkForNameMatch}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="John Smith"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                    Phone <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="tel"
                                    required
                                    value={formData.customerPhone}
                                    onChange={e => { setFormData({ ...formData, customerPhone: e.target.value }); setExistingMatch(null); }}
                                    onBlur={checkForPartialMatch}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="07700 123456"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                    Email <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={formData.customerEmail}
                                    onChange={e => { setFormData({ ...formData, customerEmail: e.target.value }); setExistingMatch(null); }}
                                    onBlur={checkForPartialMatch}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="john@example.com"
                                />
                            </div>
                        </div>

                        {/* Partial Match Detection Banner */}
                        <AnimatePresence>
                            {existingMatch && (
                                <motion.div
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    className="p-3 bg-info/10 border border-info/20 rounded-lg space-y-2"
                                >
                                    <p className="text-sm text-info">
                                        This {existingMatch.matchedBy} already belongs to <strong className="text-fg">{existingMatch.customer.full_name}</strong>
                                        {existingMatch.differs === 'phone'
                                            ? <> (phone: <span className="font-mono text-fg-3">{existingMatch.customer.phone}</span>)</>
                                            : <> (email: <span className="font-mono text-fg-3">{existingMatch.customer.email}</span>)</>
                                        }. Different {existingMatch.differs} detected.
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleUpdateExisting}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-info/20 text-info hover:bg-info/30 rounded-md transition-colors"
                                        >
                                            <RefreshCw className="w-3 h-3" />
                                            Update {existingMatch.customer.full_name}'s details
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCreateNew}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-tint/5 text-fg-3 hover:bg-tint/10 rounded-md transition-colors"
                                        >
                                            <UserPlus className="w-3 h-3" />
                                            Create new customer
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Possible-duplicate (same name) advisory — never merges automatically */}
                        <AnimatePresence>
                            {!linkedCustomer && nameMatches.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    className="p-3 bg-gold-fill/10 border border-gold/20 rounded-lg space-y-2"
                                >
                                    <p className="text-sm text-gold">
                                        {nameMatches.length === 1 ? 'A customer' : `${nameMatches.length} customers`} named
                                        {' '}<strong className="text-fg">{formData.customerName.trim()}</strong>{' '}
                                        already exist{nameMatches.length === 1 ? 's' : ''}. Same person? Reuse the record so this
                                        job attaches to them — otherwise continue and a new, separate customer is created.
                                    </p>
                                    <div className="flex flex-col gap-1.5">
                                        {nameMatches.map((c) => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => handleUseNameMatch(c)}
                                                className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs font-medium bg-tint/5 hover:bg-tint/10 rounded-md transition-colors text-left"
                                            >
                                                <span className="text-fg">{c.full_name}</span>
                                                <span className="font-mono text-fg-3 truncate">{c.phone || c.email || 'no contact'}</span>
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => setNameMatches([])}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-transparent text-fg-3 hover:text-fg transition-colors self-start"
                                        >
                                            <UserPlus className="w-3 h-3" />
                                            Continue as a new customer
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Vehicle Details (Auto-filled, editable for override + manual fields) */}
                    <div className="min-w-0 bg-tint/5 rounded-lg p-4 space-y-4">
                        <h3 className="text-sm font-bold text-fg-2 flex items-center gap-2">
                            <Car className="w-4 h-4" />
                            Vehicle Details
                            {vehicleConfirmed && formData.vehicleSource === 'db' && (
                                <span className="text-[10px] font-bold text-success bg-success/10 px-2 py-0.5 rounded border border-success/20 uppercase">Local Database</span>
                            )}
                            {vehicleConfirmed && formData.vehicleSource === 'dvla' && (
                                <span className="text-[10px] font-bold text-info bg-info/10 px-2 py-0.5 rounded border border-info/20 uppercase">DVLA API</span>
                            )}
                            {vehicleConfirmed && formData.vehicleSource === 'manual' && (
                                <span className="text-[10px] font-bold text-fg-3 bg-tint/5 px-2 py-0.5 rounded border border-line-glow/20 uppercase">Manual Entry</span>
                            )}
                        </h3>
                        {vrmOtherOwners.length > 0 && (
                            <div className="p-2.5 bg-gold-fill/10 border border-gold/20 rounded-lg text-xs text-gold/90">
                                This reg is already on record for <strong className="text-fg">{vrmOtherOwners.join(', ')}</strong>. A shared or sold car is fine — just check you haven't mistyped the plate.
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                    Make <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.make}
                                    onChange={e => setFormData({ ...formData, make: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Ford"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                    Model <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.model}
                                    onChange={e => setFormData({ ...formData, model: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Focus"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Colour</label>
                                <input
                                    type="text"
                                    value={formData.colour}
                                    onChange={e => setFormData({ ...formData, colour: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="Black"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                    Mileage
                                </label>
                                <input
                                    type="number"
                                    inputMode="numeric"
                                    value={formData.mileage}
                                    onChange={e => {
                                        setFormData({ ...formData, mileage: e.target.value });
                                        setMileageFromMotOn(null);
                                    }}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none font-mono"
                                    placeholder="45000"
                                />
                                {mileageFromMotOn !== null ? (
                                    <p className="mt-1 text-[10px] text-gold/80">
                                        From the last MOT{mileageFromMotOn
                                            ? ` (${new Date(mileageFromMotOn).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })})`
                                            : ''} — overwrite it with today's if the car has moved since.
                                    </p>
                                ) : !formData.mileage && motOffer ? (
                                    // One tap instead of typing it out, and one tap
                                    // instead of the app deciding for them. What lands
                                    // in this box is stored as the vehicle's mileage,
                                    // so it has to be a number the user has agreed to.
                                    <MotMileageOffer
                                        miles={motOffer.miles}
                                        testDate={motOffer.testDate}
                                        onUse={() => {
                                            setFormData(prev => ({ ...prev, mileage: String(motOffer.miles) }));
                                            setMileageFromMotOn(motOffer.testDate ?? '');
                                        }}
                                    />
                                ) : !formData.mileage && (
                                    <p className="mt-1 text-[10px] text-info">💡 Add mileage for service tracking</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                    Year <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="number"
                                    required
                                    min="1900"
                                    max="2030"
                                    value={formData.year}
                                    onChange={e => setFormData({ ...formData, year: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none font-mono"
                                    placeholder="2020"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Engine</label>
                                <input
                                    type="text"
                                    value={formData.engine}
                                    onChange={e => setFormData({ ...formData, engine: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none"
                                    placeholder="2.0 TDI"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Fuel Type</label>
                                <input
                                    type="text"
                                    value={formData.fuelType}
                                    onChange={e => setFormData({ ...formData, fuelType: e.target.value })}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none uppercase"
                                    placeholder="DIESEL"
                                />
                            </div>
                        </div>
                    </div>

                    </div>
                    {/* Job Description (with Template) */}
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-xs font-bold text-fg-4 uppercase">
                                Job Description
                            </label>
                            {templates.length > 0 && (
                                <select
                                    className="bg-raised text-xs text-fg-2 border border-line-strong rounded px-2 py-1 outline-none"
                                    value={selectedTemplate}
                                    onChange={(e) => handleTemplateChange(e.target.value)}
                                >
                                    <option value="">-- Load Template --</option>
                                    {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.title} (£{t.estimated_price})</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <textarea
                            value={formData.jobDescription}
                            onChange={e => setFormData({ ...formData, jobDescription: e.target.value })}
                            rows={3}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none resize-none"
                            placeholder="Full service, brake pads, MOT..."
                        />
                    </div>

                    {/* Conflict/Warning Display */}
                    <AnimatePresence>
                        {conflictWarning && !error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="p-3 bg-gold-fill/10 border border-gold/20 rounded-lg flex items-start gap-2 text-gold text-sm"
                            >
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>{conflictWarning}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Error Display */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm"
                            >
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>


                </div>
                )}
        </Modal>
    );
}
